import React, { useEffect, useRef, useState } from 'react';
import {
	Alert,
	Avatar,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	Pagination,
	Snackbar,
	Typography,
} from '@mui/material';
import {
	capitalizeFirstLetter,
	formatPriceValue,
	generateInitialsHelper,
	getStatusStyles,
	setOrderStatus,
} from '@src/helpers/Helpers'; // Make sure to adjust the import path
import { useAppSelector } from '@src/store/hooks';
import CustomAvatar from '../../components/CustomAvatar';
import CustomButton from '../../components/CustomButton/CustomButton';
import CustomDataGrid from '../../components/CustomDataGrid/CustomDataGrid';
import DateFilterMenu from '../../components/DateFilterMenu/DateFilterMenu';
import { ArrowBack, LockIcon, MarketplaceShopIcon } from '../../components/Icons';
import OrderStatusFilter from '../../components/OrderStatusFilter/OrderStatusFilter';
import SearchBar from '../../components/SearchBar';
import PageHeader from '../../components/PageHeader/PageHeader';
import CustomDataGrid2 from '../../components/CustomDataGrid/CustomDataGrid2';
import CustomModal from '../../components/CustomModal/CustomModal';
import ViewOrder from '../../components/OrderView/ViewOrder';
import { Order } from '../../components/Main/Chat/ChatMessage/ChatMessage';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CustomAlertType } from '@src/pages/Catalogs';
import Papa from 'papaparse';
import DataNotFound from '../../components/DataItemFound/DataNotFound';
import { clearUserSession } from '@src/helpers/ApiHelper';
import { IsMobileWidth, IsTabletWidth } from '../../components/MediaQueryUtils/index';
import moment from 'moment';
import CustomDrawer from '../../components/CustomDrawer/CustomDrawer';
import CustomAlert from '../../components/CustomAlert/CustomAlert';
import { RESTAURANT } from '@src/Constants';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomPermission from '../../components/Permissions/CustomPermission';
import Loading from '../../components/Loading/Loading';
import CustomCheckbox from '@src/components/Checkbox/CustomCheckbox';
import { SelectedItemsTextContainer } from '@src/components/CommonComponents/Common';
import CustomTextButton from '@src/components/CustomButton/CustomTextButton';
import styled from 'styled-components';
import OrderCard from '@src/components/CustomCard/OrderCard';
import SlidableDrawer from '@src/components/CustomModal/SlidableDrawer';
import OrderList from '@src/components/List/OrderList';
import { Product, ProductResponse, TimeoutHandler } from '@src/interfaces/type';

const StyledContainer = styled('div')({
	overflow: 'hidden',
	height: '100dvh',
	'@media (max-width: 600px)': {
		marginBottom: '68px',
	},
	flexShrink: 0,
	'#filter-container': {
		display: 'flex',
		gap: '12px',
		padding: '16px',
		flexShrink: 0,
	},
});

const ExportButtonStyles = styled('div')({
	'@media (max-width: 1024px)': {
		marginLeft: '16px',
	},
	'@media (max-width: 478px)': {
		background: 'white',
		display: 'flex',
		marginLeft: '0px',
		position: 'fixed',
		bottom: 0,
		zIndex: '100',
		width: '100%',
		padding: '16px',
		left: 0,
		borderRadius: '6px 6px 0px 0px',
		borderTop: '1.5px solid #F1F1F1',
	},
});

const FilterContainerStyles = styled('div')({
	display: 'flex',
	alignItems: 'center',
	'@media (max-width: 1024px)': {
		flexWrap: 'wrap',
	},
});

const gridResponsiveStyles: any = {
	'@media (max-width: 478px)': {
		maxHeight: 'calc(100vh - 300px)',
		minHeight: 'calc(100vh - 300px)',
		paddingBottom: '25px',
	},
};

const SelectedItemsContainer = styled('div')({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '5px',
	fontSize: '16px',
	fontWeight: 750,
	lineHeight: '21.82px',
	transition: 'width 0.3s ease',
});

const Container = styled.div`
	background: #f1f1f1;
	padding-top: 2px;
	overflow-y: auto;
	flex-grow: 1;
`;

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 100;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 0;
`;

export interface SelectedOptions {
	[status: string]: boolean;
}

export interface FilterOptions {
	label: string;
}

interface Props {
	filteredRows: any;
	setFilteredRows: any;
	setSearchValue: any;
	searchValue: any;
	orders: any;
	setOrders: any;
	collapse: any;
}

const Orders: React.FC<Props> = ({
	filteredRows,
	setFilteredRows,
	setSearchValue,
	searchValue,
	orders,
	setOrders,
	collapse,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const allOrders: any = useAppSelector((state) => state?.order?.allOrders);
	const pullToRefresh: any = useAppSelector((state) => state?.UI?.pullToRefresh);
	const { id } = useParams();

	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const orderIdQueryParam = queryParams.get('orderId');

	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isErrorVisible, setErrorVisible] = useState(false);
	const [alert, setAlert] = useState<CustomAlertType>({ message: '', severity: 'success' });
	const [errorMessage, setErrorMessage] = useState('');
	const isTabletWidth = IsTabletWidth();
	const [selectedOptions, setSelectedOptions] = useState<SelectedOptions | null>(null);
	const [selectedOption, setSelectedOption] = useState<string>('All');
	const [viewOrder, setViewOrder] = useState(false);
	const [orderDetails, setOrderDetails] = useState<Order | null>(null);
	const [orderInfo, setOrderInfo] = useState(null);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [shareModal, setShareModal] = useState(false);
	const [filters, setFilters] = useState<{
		fromDate: Date | null;
		toDate: Date | null;
		status: string | null;
	}>({
		fromDate: null,
		toDate: null,
		status: '',
	});

	const [statusFilterOptions, setStatusFilterOptions] = useState<FilterOptions[]>([]);
	const [selectAll, setSelectAll] = React.useState(false);
	const [apiCall, setApiCall] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [searching, setSearching] = useState<boolean>(false);
	const [loadingOrders, setLoadingOrders] = useState<boolean>(false);

	//Filters
	const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
	const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

	const { isDrawerOpen } = useAppSelector((state) => state?.UI);
	const isMobileWidth = IsMobileWidth();

	const timeoutHandler = useRef<TimeoutHandler>(null);

	//Permission
	const checkPermission = usePermission();
	const viewOrderPermission = checkPermission(ALL_PERMISSION_MAP?.GENERAL?.ORDER?.VIEW, id);

	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		transition: 'width 0.3s ease',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};
	const isRestaurantType = currentUser?.type?.toLowerCase() === RESTAURANT;
	const renderAvatar = (
		imageSrc: string | null,
		altText = '',
		fallbackText = '',
		width = '26px',
		height = '26px'
	) => (
		<Avatar
			alt={altText}
			src={imageSrc || ''}
			sx={{
				marginRight: 1,
				width: width,
				height: height,
				img: {
					objectFit: 'contain',
				},
			}}
		>
			{fallbackText || (altText && altText?.charAt(0))}
		</Avatar>
	);

	const columns = [
		// {

		// 	field: 'id',

		// 	headerName: 'Order ID',

		// 	width: 200,

		// 	headerAlign: 'center',

		// 	align: 'center',

		// },
		{
			field: 'name',
			headerName: isRestaurantType ? 'Supplier' : 'Customer',
			width: 150,
			renderCell: (params: any) => {
				return (
					<Typography>
						{isRestaurantType ? params.row?.supplier?.name : params.row?.restaurant?.name}
					</Typography>
				);
			},
		},
		{
			field: 'soId',
			headerName: 'SO Number',
			width: 200,
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'statusData',
			headerName: 'Order Status',
			width: 168,
			renderCell: (params: any) => (
				<Chip
					label={params.row?.statusData?.name}
					style={{
						backgroundColor: params.row?.statusData?.color_bg,
						color: params?.row?.statusData?.color,
						fontWeight: 'bold',
						borderRadius: '4px',
					}}
				/>
			),
		},

		// {
		// 	field: 'firstname',

		// 	headerName: currentUser?.type?.toLowerCase() === RESTAURANT ? 'Supplier' : 'Dealer',
		// 	width: 300,
		// 	renderCell: (params: any) => {
		// 		const avatarComponent = isRestaurantType
		// 			? params?.row?.supplier?.image
		// 				? renderAvatar(params?.row?.supplier?.image, params?.row?.firstname)
		// 				: renderAvatar(null, params?.value)
		// 			: params?.row?.restaurant?.image
		// 			? renderAvatar(params?.row?.restaurant.image, params?.row?.firstname)
		// 			: renderAvatar(null, params?.value);

		// 		return (
		// 			<Box sx={{ display: 'flex', alignItems: 'center' }}>
		// 				{avatarComponent}
		// 				{params.value}
		// 			</Box>
		// 		);
		// 	},
		// },
		{
			field: 'orderDate',
			headerName: 'Ordered',
			width: 230,
		},
		{
			field: 'deliveryDate',
			headerName: 'Delivery Date',
			width: 200,
			renderCell: (params: any) => {
				if (params.row?.deliveryDate === 'Invalid date') {
					return <Typography>N/A</Typography>;
				}
				return <Typography>{params.row?.deliveryDate}</Typography>;
			},
		},
		{
			field: 'mobileNumber',
			headerName: 'Mobile Number',
			width: 180,
			renderCell: (params: any) => {
				return (
					<Typography>
						{isRestaurantType
							? params.row?.supplier?.customer?.telephone
							: params?.row?.restaurant?.customer?.telephone}
					</Typography>
				);
			},
		},
		{
			field: 'paymentStatus',
			headerName: 'Payment Status',
			width: 168,
			renderCell: (params: any) => {
				if (!params.row?.paymentStatus?.name) {
					return null;
				}
				const bgMap: any = {
					Paid: {
						color: '#00BFA5',
						color_bg: '#E0F3F2',
					},
					'Partially Paid': {
						color: '#FFC107',
						color_bg: '#FFF3E0',
					},
					Unpaid: {
						color: '#F44336',
						color_bg: '#FFEBEE',
					},
					Overdue: {
						color: '#F44336',
						color_bg: '#FFEBEE',
					},
				};
				const name = params.row?.paymentStatus?.name;
				const color = bgMap[name]?.color;
				const color_bg = bgMap[name]?.color_bg;
				return (
					<Chip
						label={name}
						style={{
							backgroundColor: color_bg,
							color: color,
							fontWeight: 'bold',
							borderRadius: '4px',
						}}
					/>
				);
			},
		},
		{
			field: 'creditStatus',
			headerName: 'Credit Status',
			width: 168,
			renderCell: (params: any) => {
				const { isActive } = params.row?.credits;
				const label = isActive ? params.row?.creditStatus.split('_').join(' ') : 'INACTIVE';
				const color = isActive
					? params?.row?.creditStatus === 'AVAILABLE'
						? 'green'
						: 'red'
					: 'red';
				return (
					<Chip
						label={label}
						style={{
							// backgroundColor: params.row?.statusData?.color_bg,
							color: color,
							fontWeight: 'bold',
							borderRadius: '4px',
						}}
					/>
				);
			},
		},
		{
			field: 'isMarketplace',
			headerName: 'Marketplace',
			width: 168,
			renderCell: (params: any) =>
				params?.row?.isMarketplace === 1 ? <MarketplaceShopIcon /> : null,
		},
	];

	const getFilterData = (
		selectedOption: string,
		selectedOptions: SelectedOptions,
		rows: Order[],
		customStartDate: Date | null,
		customEndDate: Date | null
	) => {
		let { fromDate, endDate }: any = filterRowsByDate(
			selectedOption,
			rows,
			customStartDate,
			customEndDate
		);
		filterRowsByStatus(selectedOptions, fromDate, endDate);
	};

	const filterRowsByDate = (
		selectedOption: string,
		rows: Order[],
		customStartDate: Date | null,
		customEndDate: Date | null
	) => {
		const currentDate = moment();

		// const filterRows = (condition: (date: moment.Moment) => boolean) => {
		// 	const filteredRows = rows?.filter((row: any) => {
		// 		const orderDate = moment(row.orderDate, 'DD-MMMM-YYYY');
		// 		return condition(orderDate);
		// 	});
		// 	return filteredRows;
		// };

		switch (selectedOption) {
			case 'Today':
				return {
					fromDate: currentDate.startOf('day').toDate(),
					endDate: currentDate.endOf('day').toDate(),
				};
			case 'Last Week':
				const lastWeekDate = moment().subtract(7, 'days').startOf('day').toDate();
				return { fromDate: lastWeekDate, endDate: currentDate.endOf('day').toDate() };
			case 'Last Month':
				const lastMonthDate = moment().subtract(1, 'month').startOf('day').toDate();
				return { fromDate: lastMonthDate, endDate: currentDate.endOf('day').toDate() };
			case 'Custom':
				// const startDate = moment(new Date(customStartDate as Date));
				// const endDate = moment(new Date(customEndDate as Date));
				return { fromDate: customStartDate, endDate: customEndDate };
			default:
				return { fromDate: null, endDate: null };
		}
	};

	const filterRowsByStatus = (
		selectedOptions: SelectedOptions,
		startDate: Date | null,
		endDate: Date | null
	) => {
		// Get the keys whose values are true and convert them to lowercase

		const selectedIds = selectedOptions
			? statusFilterOptions
					.filter((option) => selectedOptions[option?.label])
					//@ts-ignore
					.map((option) => option?.id)
					.join(',')
			: null;

		setPage(1);
		setFilters({
			fromDate: startDate,
			toDate: endDate,
			status: selectedIds,
		});
		setLoadingOrders(true);
	};

	const handleSelectAll = () => {
		if (selectAll) {
			setSelectedItemIds([]);
		} else {
			const allOrderIds = orders?.map((order: Order) => order.id);
			setSelectedItemIds(allOrderIds);
		}
		setSelectAll(!selectAll);
	};

	const isSelected = (orderId: string) => selectedItemIds?.includes(orderId);

	const toggleSelect = (orderId: string, e: any) => {
		if (isSelected(orderId)) {
			setSelectedItemIds(selectedItemIds.filter((id) => id !== orderId));
		} else {
			setSelectedItemIds([...selectedItemIds, orderId]);
		}
	};

	const handleCheckboxChange = (id: string, e: any) => {
		toggleSelect(id, e);
	};

	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
	};

	const onRowsSelectionHandler = (ids: string[]) => {
		setSelectedItemIds(ids);
	};

	const loadMore = () => {
		setLoadingOrders(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	const viewDetails = (order: Order, e?: any) => {
		if (e?.target?.tagName === 'INPUT') {
			return;
		}
		const updatedOrderProducts = order?.products?.map((item: Product) => {
			return {
				...item,
				id: item?.productId,
				price: formatPriceValue(item?.price),
				subCategory: item?.subCategory?.name,
				subCategoryId: item?.subCategory?.id,
				maxPrice: formatPriceValue(item?.maxPrice),
				machineBrand: item?.machineBrand?.name,
				machineBrandId: item?.machineBrand?.id,
				machineModel: item?.machineModel?.name,
				machineModelId: item?.machineModel?.id,
				groupCategory: item?.groupCategory?.name,
				groupCategoryId: item?.groupCategory?.id,
				brand: item?.brand?.name,
				brandId: item?.brand?.id,
				partComposition: item?.partComposition?.name,
				partCompositionId: item?.partComposition?.id,
				partDesign: item?.partDesign?.name,
				partDesignId: item?.partDesign?.id,
				partDiameter: item?.partDiameter?.name,
				partDiameterId: item?.partDiameter?.id,
				partDimension: item?.partDimension?.name,
				partDimensionId: item?.partDimension?.id,
				partOwnership: item?.partOwnership?.name,
				partOwnershipId: item?.partOwnership?.id,
				partQuality: item?.partQuality?.name,
				partQualityId: item?.partQuality?.id,
				partWeight: item?.partWeight?.name,
				partWeightId: item?.partWeight?.id,
				partPerformance: item?.partPerformance?.name,
				partPerformanceId: item?.partPerformance?.id,
			};
		});
		//@ts-ignore
		setOrderDetails({
			...order,
			//@ts-ignore
			orderDate: order?.orderDateWithTime,
			//@ts-ignore
			requestedDeliveryDate: order?.deliveryDateWithTime,
			products: updatedOrderProducts,
			orderStatus: {
				color: order?.statusData?.color,
				color_bg: order?.statusData?.color_bg,
				name: order?.statusData?.name,
				//@ts-ignore
				id: order?.statusData?.id,
				validation_order: order?.statusData?.validation_order,
				//@ts-ignore
				group_id: order?.statusData?.group_id,
			},
		});
		setViewOrder(true);
	};

	const handleRowClick = (params: any) => {
		const updatedOrderProducts = params?.row?.products?.map((item: ProductResponse) => {
			return {
				...item,
				id: item?.productId,
				price: formatPriceValue(item?.price),
				quantity: item?.quantity,
				category: item?.category?.name,
				categoryId: item?.category?.id,
				subCategory: item?.subCategory?.name,
				subCategoryId: item?.subCategory?.id,
				maxPrice: formatPriceValue(item?.maxPrice),
				machineBrand: item?.machineBrand?.name,
				machineBrandId: item?.machineBrand?.id,
				machineModel: item?.machineModel?.name,
				machineModelId: item?.machineModel?.id,
				groupCategory: item?.groupCategory?.name,
				groupCategoryId: item?.groupCategory?.id,
				brand: item?.brand?.name,
				brandId: item?.brand?.id,
				partComposition: item?.partComposition?.name,
				partCompositionId: item?.partComposition?.id,
				partDesign: item?.partDesign?.name,
				partDesignId: item?.partDesign?.id,
				partDiameter: item?.partDiameter?.name,
				partDiameterId: item?.partDiameter?.id,
				partDimension: item?.partDimension?.name,
				partDimensionId: item?.partDimension?.id,
				partOwnership: item?.partOwnership?.name,
				partOwnershipId: item?.partOwnership?.id,
				partQuality: item?.partQuality?.name,
				partQualityId: item?.partQuality?.id,
				partWeight: item?.partWeight?.name,
				partWeightId: item?.partWeight?.id,
				partPerformance: item?.partPerformance?.name,
				partPerformanceId: item?.partPerformance?.id,
			};
		});
		setOrderDetails({
			...params?.row,
			orderDate: params?.row?.orderDateWithTime,
			requestedDeliveryDate: params?.row?.deliveryDateWithTime,
			products: updatedOrderProducts,
			orderStatus: {
				color: params?.row?.statusData?.color,
				color_bg: params?.row?.statusData?.color_bg,
				name: params?.row?.statusData?.name,
				id: params?.row?.statusData?.id,
				validation_order: params?.row?.statusData?.validation_order,
				group_id: params?.row?.statusData?.group_id,
			},
		});
		setViewOrder(true);
	};

	const handleClose = () => {
		// Parse the current URL
		const url = new URL(window.location.href);

		// Remove the orderId parameter
		url.searchParams.delete('orderId');

		// Update the URL in the address bar
		window.history.replaceState(null, '', url.toString());
		setViewOrder(false);
	};

	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const displayCustomError = (error: AxiosError) => {
		const msg = error?.response?.data?.message || 'Something went wrong!';
		setErrorMessage(msg);
		setErrorVisible(true);
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			setSelectedItemIds([]);
			setLoadingOrders(true);
		}
	};

	const exportToCsv = (tableData: any[], forSharing = false) => {
		const updatedData = tableData?.filter((data: any) =>
			selectedItemIds?.includes(data?.id?.toString())
		);

		const csvRows: any[] = [];

		updatedData?.forEach((order: any) => {
			csvRows.push({
				'Order ID': order?.timeId || order?.orderId,
				'Supplier Name': order?.supplier?.name,
				'Dealer Name': order?.restaurant?.name,
				Ordered: order?.orderDate,
				'Deliver Date': order?.deliveryDate,
				'Mobile Number': isRestaurantType
					? String(order?.supplier?.customer?.telephone)
					: String(order?.restaurant?.customer?.telephone),
				'Order Status': order?.orderStatus,
				'Payment Status': order?.paymentStatus?.name || '',
				'Credit Status': order?.creditStatus?.split('_').join(' ') || '',
			});

			order?.products?.forEach((product: any, index: number) => {
				csvRows.push({
					'Product ID': product?.order_product_id,
					'Part Name': product.name,
					Quantity: product?.quantity,
					Unit: product?.unit,
					'Price (INR)': product?.price,
					'Total (INR)': product?.total,
				});
			});

			csvRows.push({
				'Order Total (INR)': order?.total,
			});
		});

		const csvData = Papa.unparse({
			fields: [
				'Order ID',
				'Supplier Name',
				'Dealer Name',
				'Ordered',
				'Deliver Date',
				'Mobile Number',
				'Order Status',
				'Payment Status',
				'Credit Status',
				'Product ID',
				'Part Name',
				'Quantity',
				'Unit',
				'Price (INR)',
				'Total (INR)',
				'Order Total (INR)',
			],
			data: csvRows,
		});

		if (!forSharing) {
			const blob = new Blob([csvData], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = 'order_list_with_products.csv';
			a.click();
			window.URL.revokeObjectURL(url);
			setShareModal(false);
		}

		return csvData;
	};

	const shareData = async (tableData: any[]) => {
		const csvData = exportToCsv(tableData, true);

		if (navigator?.share) {
			const csvBlob = new Blob([csvData], { type: 'text/csv' });
			const url = window.URL.createObjectURL(csvBlob);
			const shareData = {
				title: 'Order List with Products',
				text: 'Here is the order list with products CSV file.',
				files: [new File([csvBlob], 'order_list_with_products.csv', { type: 'text/csv' })],
			};
			try {
				await navigator.share(shareData);
				console.log('Share Successful');
				window.URL.revokeObjectURL(url);
				setShareModal(false);
			} catch (error) {
				console.error('Sharing failed', error);
			}
		} else {
			window?.alert('Web Share not supported');
		}
	};

	const setStatusesOptions = (filterOptions: FilterOptions[], newStatus: any) => {
		const labelSet = new Set(filterOptions?.map((option) => option?.label));
		if (!labelSet.has(newStatus)) {
			filterOptions?.push({ label: capitalizeFirstLetter(newStatus) });
		}
		console.log(filterOptions);
		return filterOptions;
	};

	const getAllOrders = () => {
		const limit = 25;

		if (viewOrderPermission) {
			const ordersPromise = new Promise((resolve, reject) => {
				apiService.getAllOrders(
					(response: AxiosResponse) => {
						const data = response?.data?.data;
						const updatedOrders: any = data?.orders?.map((order: any) => {
							return {
								...order,
								firstname:
									currentUser?.type?.toLowerCase() === RESTAURANT
										? order?.supplier?.name
										: order?.restaurant?.name,
								id: order?.timeId?.toString() || order?.orderId?.toString(),
								orderStatus: capitalizeFirstLetter(order?.orderStatus?.name),
								orderDate: moment(order?.orderDate).format('DD-MM-YY, hh:mm a'),
								requestedDeliveryDate: isMobileWidth
									? moment(order?.requestedDeliveryDate).format('DD-MM-YY')
									: moment(order?.requestedDeliveryDate).format('DD-MMMM-YYYY'),
								deliveryDate: isMobileWidth
									? moment(order?.requestedDeliveryDate).format('DD-MM-YY')
									: moment(order?.requestedDeliveryDate).format('DD-MMMM-YYYY'),
								statusData: order?.orderStatus,
								deliveryDateWithTime: moment(order?.requestedDeliveryDate).format('MMM DD, YYYY'),
								orderDateWithTime: moment(order?.orderDate).format('MMM DD, YYYY hh:mma'),
								soId: order?.orderFulfillmentLifeCycle?.accepted?.salesOrderNumber || 'N/A',
							};
						});
						const totalCount: number = data?.totalCount;
						const totalPages: number = Math.max(Math.ceil(totalCount / limit), 1);

						resolve({ updatedOrders, totalCount, totalPages });
					},
					(error: AxiosError) => {
						console.log(error);
						reject(error);
					},
					id,
					{
						page: page,
						limit: limit,
						search: searchValue,
						from: filters?.fromDate
							? moment(new Date(filters?.fromDate)).format('YYYY-MM-DD')
							: null,
						to: filters?.toDate ? moment(new Date(filters?.toDate)).format('YYYY-MM-DD') : null,
						status: filters?.status,
					}
				);
			});

			const orderStatusesPromise = new Promise<any>((resolve, reject) => {
				apiService.getOrderStatuses(
					(res: AxiosResponse) => {
						const data = res?.data?.data;
						const allOrderStatuses = data?.flatMap((group: any) =>
							group.order_statuses.map((status: any) => ({
								id: status.order_status_id,
								label: status.name,
							}))
						);
						resolve(allOrderStatuses);
					},
					(err: AxiosError) => {
						console.log(err);
						reject(err);
					}
				);
			});

			Promise.all([ordersPromise, orderStatusesPromise])
				.then(([ordersResult, allOrderStatuses]) => {
					const { updatedOrders, totalCount, totalPages }: any = ordersResult;

					if (orderDetails) {
						const order = updatedOrders?.find(
							(item: Order) => item?.id?.toString() === orderDetails?.id?.toString()
						);

						viewDetails(order);
					}

					if (page === 1) {
						setOrders(updatedOrders);
					} else {
						setOrders((prevState: any) => [...prevState, ...updatedOrders]);
					}

					if (apiCall) {
						setApiCall(false);
					}

					setTotalPages(totalPages);
					setTotalCount(totalCount);
					setStatusFilterOptions(allOrderStatuses);
					setIsLoading(false);
					setLoadingOrders(false);
					setSearching(false);

					if (apiCall) {
						setApiCall(false);
					}

					// Check orderId from query param
					if (orderIdQueryParam) {
						const order = updatedOrders?.find(
							(item: Order) => item?.id?.toString() === orderIdQueryParam?.toString()
						);

						viewDetails(order);
					}
				})
				.catch((error) => {
					console.log(error);
					displayCustomError(error);
					setIsLoading(false);
					setLoadingOrders(false);
					setSearching(false);
				});
		} else {
			setIsLoading(false);
		}
	};

	// useEffect(() => {
	// 	//persisted data
	// 	setOrders(allOrders);
	// 	setFilteredRows(allOrders);
	// 	if (allOrders?.length) {
	// 		setIsLoading(false);
	// 	}
	// }, [allOrders]);

	useEffect(() => {
		getAllOrders();
	}, [apiCall, viewOrderPermission, page, filters]);

	useEffect(() => {
		//debounce
		if (timeoutHandler.current) {
			clearTimeout(timeoutHandler.current);
		}
		timeoutHandler.current = setTimeout(() => {
			try {
				getAllOrders();
			} catch (err) {}
		}, 500);
	}, [searchValue]);

	const modals = () => {
		if (shareModal) {
			return (
				<CustomModal
					open={shareModal}
					setOpen={setShareModal}
					title="Share"
					style={{}}
					children={
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px' }}>
							<CustomButton
								color="primary"
								title="Share"
								handleClick={() => shareData(orders)}
								styles={{
									width: '130px',
									height: '36px',
									borderRadius: '4px',
									border: '1px solid #FFC51A',
									background: '#ffffff !important',
									color: 'var(--default-text-color) !important',
									':hover': {
										background: '#f0f0f0 !important',
									},
								}}
							/>
							<CustomButton
								color="primary"
								title="Export"
								handleClick={() => {
									exportToCsv(orders);
								}}
								styles={{
									width: '130px',
									height: '36px',
									borderRadius: '4px',
									border: '1px solid #FFC51A',
									background: '#ffffff !important',
									color: 'var(--default-text-color) !important',
									':hover': {
										background: '#f0f0f0 !important',
									},
								}}
							/>
						</div>
					}
				/>
			);
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	// if (isMobileWidth) {
	// 	return (
	// 		<StyledContainer style={pageContainerStyles}>
	// 			{viewOrderPermission ? (
	// 				<>
	// 					{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
	// 					{viewOrder && !isMobileWidth ? (
	// 						<CustomDrawer
	// 							open={viewOrder}
	// 							setOpen={setViewOrder}
	// 							style={{ width: isTabletWidth ? '100%' : '1118px' }}
	// 							header={false}
	// 						>
	// 							{orderDetails && (
	// 								<ViewOrder
	// 									alert={alert}
	// 									setAlert={setAlert}
	// 									orderDetails={orderDetails}
	// 									setOrderDetails={setOrderDetails}
	// 									orderInfo={orderInfo}
	// 									setOrderInfo={setOrderInfo}
	// 									currentUser={currentUser}
	// 									title={orderDetails.firstname}
	// 									handleClose={handleClose}
	// 									orders={orders}
	// 									setOrders={setOrders}
	// 									displayCustomError={displayCustomError}
	// 									setApiCall={setApiCall}
	// 								/>
	// 							)}
	// 						</CustomDrawer>
	// 					) : (
	// 						<SlidableDrawer
	// 							isOpen={viewOrder}
	// 							setIsOpen={setViewOrder}
	// 							onClose={() => {
	// 								setOrderDetails(null);
	// 								setViewOrder(false);
	// 							}}
	// 						>
	// 							{' '}
	// 							{orderDetails && (
	// 								<ViewOrder
	// 									alert={alert}
	// 									setAlert={setAlert}
	// 									orderDetails={orderDetails}
	// 									setOrderDetails={setOrderDetails}
	// 									orderInfo={orderInfo}
	// 									setOrderInfo={setOrderInfo}
	// 									currentUser={currentUser}
	// 									title={orderDetails.firstname}
	// 									handleClose={handleClose}
	// 									orders={orders}
	// 									setOrders={setOrders}
	// 									displayCustomError={displayCustomError}
	// 									setApiCall={setApiCall}
	// 								/>
	// 							)}
	// 						</SlidableDrawer>
	// 					)}
	// 					<Box
	// 						display="flex"
	// 						justifyContent="space-between"
	// 						alignItems={isTabletWidth ? 'flex-start' : 'center'}
	// 						maxWidth="100%"
	// 						sx={{ px: 2 }}
	// 					>
	// 						<FilterContainerStyles>
	// 							<DateFilterMenu
	// 								rows={orders}
	// 								selectedOption={selectedOption}
	// 								selectedOptions={selectedOptions}
	// 								setSelectedOption={setSelectedOption}
	// 								customStartDate={customStartDate}
	// 								customEndDate={customEndDate}
	// 								setCustomStartDate={setCustomStartDate}
	// 								setCustomEndDate={setCustomEndDate}
	// 								getFilterData={getFilterData}
	// 								style={{ margin: isTabletWidth ? '16px 16px 0px 0px' : '0px 16px' }}
	// 							/>

	// 							<OrderStatusFilter
	// 								rows={orders}
	// 								selectedOption={selectedOption}
	// 								selectedOptions={selectedOptions}
	// 								setSelectedOptions={setSelectedOptions}
	// 								customStartDate={customStartDate}
	// 								customEndDate={customEndDate}
	// 								getFilterData={getFilterData}
	// 								statusFilterOptions={statusFilterOptions}
	// 								style={{ marginTop: isTabletWidth ? '16px' : '0px' }}
	// 							/>
	// 						</FilterContainerStyles>
	// 					</Box>

	// 					<div id="filter-container">
	// 						{filteredRows?.length ? (
	// 							<CustomTextButton
	// 								style={{
	// 									fontSize: '13px',
	// 									fontWeight: 700,
	// 									color: '#FF6120',
	// 									textDecoration: 'none',
	// 									maxWidth: '75px',
	// 									marginTop: '4px',
	// 								}}
	// 								title={
	// 									selectedItemIds?.length === filteredRows?.length ? 'Deselect All' : 'Select All'
	// 								}
	// 								onClick={() => handleSelectAll()}
	// 							/>
	// 						) : null}
	// 						{filteredRows?.length ? (
	// 							<SelectedItemsTextContainer
	// 								selectedItemIds={selectedItemIds}
	// 								filteredItems={filteredRows}
	// 								items
	// 							/>
	// 						) : null}
	// 					</div>
	// 					<Divider style={{ width: '100%' }} />

	// 					{orders?.length ? (
	// 						<OrderList
	// 							orders={orders}
	// 							searching={searching}
	// 							loadingOrders={loadingOrders}
	// 							loadMore={loadMore}
	// 							isSelected={isSelected}
	// 							viewDetails={viewDetails}
	// 							style={{ overflow: 'auto', height: 'auto' }}
	// 							totalCount={totalCount}
	// 							isRestaurantType={isRestaurantType}
	// 							renderAvatar={renderAvatar}
	// 							handleSpecificOrderSelect={handleCheckboxChange}
	// 							collapse={collapse}
	// 						/>
	// 					) : (
	// 						<DataNotFound content="No Orders Found" />
	// 					)}
	// 					<Snackbar
	// 						anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
	// 						open={isErrorVisible}
	// 						autoHideDuration={6000}
	// 						onClose={handleErrorClose}
	// 					>
	// 						<Alert onClose={handleErrorClose} severity="error" elevation={4}>
	// 							{errorMessage}
	// 						</Alert>
	// 					</Snackbar>
	// 				</>
	// 			) : (
	// 				<CustomPermission
	// 					icon={<LockIcon />}
	// 					title="We are sorry..."
	// 					paragraph="You do not have permission to view orders. Contact
	// 			an administrator to get permission."
	// 				/>
	// 			)}
	// 			{selectedItemIds?.length ? (
	// 				<StyledBottomBtnContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
	// 					<CustomButton
	// 						title="Export"
	// 						color="primary"
	// 						styles={{
	// 							height: '40px',
	// 							width: '320px',
	// 						}}
	// 						disabled={!selectedItemIds?.length}
	// 						handleClick={() => setShareModal(true)}
	// 					/>
	// 				</StyledBottomBtnContainer>
	// 			) : null}
	// 			{modals()}
	// 		</StyledContainer>
	// 	);
	// }

	return (
		<StyledContainer style={pageContainerStyles}>
			{viewOrderPermission ? (
				<>
					{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
					{viewOrder ? (
						<CustomDrawer
							open={viewOrder}
							setOpen={setViewOrder}
							style={{ width: isTabletWidth ? '100%' : '1118px' }}
							header={false}
						>
							{orderDetails && (
								<ViewOrder
									alert={alert}
									setAlert={setAlert}
									orderDetails={orderDetails}
									setOrderDetails={setOrderDetails}
									orderInfo={orderInfo}
									setOrderInfo={setOrderInfo}
									currentUser={currentUser}
									title={orderDetails.firstname}
									handleClose={handleClose}
									orders={orders}
									setOrders={setOrders}
									displayCustomError={displayCustomError}
									setApiCall={setApiCall}
									isOrderPage={true}
								/>
							)}
						</CustomDrawer>
					) : null}
					<Box
						display="flex"
						justifyContent="space-between"
						alignItems={isTabletWidth ? 'flex-start' : 'center'}
						maxWidth="100%"
						sx={{ px: 2, py: '16px' }}
					>
						<FilterContainerStyles>
							<SearchBar
								style={{
									width: isTabletWidth ? '100%' : '312px',
									height: '36px',
								}}
								value={searchValue}
								onChange={handleSearch}
								isLoading={false}
							/>

							<DateFilterMenu
								rows={orders}
								selectedOption={selectedOption}
								selectedOptions={selectedOptions}
								setSelectedOption={setSelectedOption}
								customStartDate={customStartDate}
								customEndDate={customEndDate}
								setCustomStartDate={setCustomStartDate}
								setCustomEndDate={setCustomEndDate}
								getFilterData={getFilterData}
								style={{ margin: isTabletWidth ? '16px 16px 0px 0px' : '0px 16px' }}
							/>

							<OrderStatusFilter
								rows={orders}
								selectedOption={selectedOption}
								selectedOptions={selectedOptions}
								setSelectedOptions={setSelectedOptions}
								customStartDate={customStartDate}
								customEndDate={customEndDate}
								getFilterData={getFilterData}
								statusFilterOptions={statusFilterOptions}
								style={{ marginTop: isTabletWidth ? '16px' : '0px' }}
							/>
						</FilterContainerStyles>
						<ExportButtonStyles>
							<CustomButton
								title="Export"
								color="primary"
								styles={{
									height: '36px',
									'@media (max-width: 478px)': {
										width: '100%',
									},
								}}
								disabled={!selectedItemIds?.length}
								handleClick={() => setShareModal(true)}
							/>
						</ExportButtonStyles>
					</Box>
					<SelectedItemsContainer style={{ justifyContent: 'flex-end', marginRight: '16px' }}>
						{selectedItemIds?.length ? (
							<Typography>
								Selected Items {selectedItemIds?.length} out of {totalCount}
							</Typography>
						) : (
							<Typography>Total Items: {totalCount}</Typography>
						)}
					</SelectedItemsContainer>
					{orders?.length ? (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '12px',
								padding: '16px',
								maxHeight: '60vh',
								minHeight: '400px',
								'@media (min-width: 475px)': {
									marginBottom: '68px',
								},
							}}
						>
							<CustomDataGrid2
								rows={orders}
								columns={columns}
								style={{
									transition: 'height 0.3s ease',
									maxHeight: 'calc(100vh - 250px)',
									minHeight: 'calc(100vh - 250px)',
								}}
								showNoRowsFound={!orders?.length}
								onRowsSelectionHandler={onRowsSelectionHandler}
								handleRowClick={handleRowClick}
								responsiveStyles={gridResponsiveStyles}
								loading={loadingOrders}
								type="server"
								filterType="server"
							/>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
									padding: '10px',
								}}
							>
								<Pagination
									color="primary"
									count={totalPages}
									page={page}
									onChange={handlePageChange}
								/>
							</Box>
						</Box>
					) : (
						<DataNotFound content="No Orders Found" />
					)}
					<Snackbar
						anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
						open={isErrorVisible}
						autoHideDuration={6000}
						onClose={handleErrorClose}
					>
						<Alert onClose={handleErrorClose} severity="error" elevation={4}>
							{errorMessage}
						</Alert>
					</Snackbar>
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph="You do not have permission to view orders. Contact 
				an administrator to get permission."
				/>
			)}
			{modals()}
		</StyledContainer>
	);
};

export default Orders;
